// src/components/pages/Pessoa/tabs/PessoaTabEmails.jsx

import { ThemeProvider } from '@mui/material';
import { Box, Button, Tooltip, IconButton } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import Input from '../../../form/Input';
import Select from '../../../form/Select';
import Checkbox from '../../../form/Checkbox';

import { usePessoaEmailModule } from '../modules/usePessoaEmailModule';

import { podeEditarItem, podeCancelarItem } from '../../../../hooks/usePermisionMRT';

export default function PessoaTabEmails({ dados, theme, mrt, pessoaEmails, setPessoaEmail, EnumTipoEmail, setFlashMessage }) {
    const { rows, draft, setDraft, columns, handleEdit, handleDelete, handleSave, resetDraft } = usePessoaEmailModule({
        pessoaId: dados?.id,
        rows: pessoaEmails || [],
        setRows: setPessoaEmail,
        setFlashMessage,
    });

    function handleDraftChange(e) {
        const { name, value, checked, type, options, selectedIndex } = e.target;
        const field = name.split('.')[1];

        setDraft((prev) => ({
            ...prev,
            [field]: field === 'tipo' ? options?.[selectedIndex]?.text || value : type === 'checkbox' ? checked : value,
        }));
    }

    return (
        <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                <Input
                    text="Novo email"
                    type="email"
                    name="novoEmail.email"
                    placeholder="email"
                    title="Digite o novo email da pessoa"
                    inputWidth="350px"
                    handleOnChange={handleDraftChange}
                    value={draft.email || ''}
                />

                <Select
                    name="novoEmail.tipo"
                    text="Tipo de email"
                    options={EnumTipoEmail}
                    handleOnChange={handleDraftChange}
                    value={draft.tipo || 'Outros'}
                    isEnum={true}
                />

                <Checkbox
                    title="Quando marcado torna o email como principal da pessoa"
                    text="Principal"
                    name="novoEmail.principal"
                    handleOnChange={handleDraftChange}
                    value={draft.principal}
                    checked={!!draft.principal}
                />

                <Checkbox
                    title="Quando marcado torna o email ativo"
                    text="Ativo"
                    name="novoEmail.ativo"
                    handleOnChange={handleDraftChange}
                    value={draft.ativo}
                    checked={!!draft.ativo}
                />

                <Button variant="contained" color="inherit" onClick={resetDraft}>
                    Limpar
                </Button>

                <Button variant="contained" color="info" onClick={handleSave} disabled={!draft.email}>
                    {draft.funcao || 'Adicionar'}
                </Button>
            </Box>

            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    displayColumnDefOptions={{
                        'mrt-row-actions': {
                            muiTableHeadCellProps: { align: 'center' },
                            size: 100,
                        },
                        'mrt-row-numbers': {
                            enableColumnOrdering: true,
                            enableResizing: true,
                            muiTableHeadCellProps: { sx: { fontSize: '1.2rem' } },
                        },
                        'mrt-row-select': {
                            enableColumnActions: false,
                            enableHiding: true,
                            size: 50,
                        },
                    }}
                    enableColumnFilterModes
                    enableGlobalFilterModes
                    enableColumnOrdering
                    enableEditing
                    renderRowActions={({ row }) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                            <Tooltip arrow placement="left" title="Editar">
                                <span>
                                    <IconButton
                                        onClick={() => handleEdit(row.original, row.index, 'Editar')}
                                        disabled={!podeEditarItem()}
                                    >
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip arrow placement="right" title="Deletar">
                                <span>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDelete(row.original, row.index, 'Deletar')}
                                        disabled={!podeCancelarItem()}
                                    >
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                    columns={columns}
                    data={rows || []}
                    rowCount={mrt.rowCount}
                    enableRowSelection
                    getRowId={(row) => row.uuid}
                    manualFiltering
                    manualPagination
                    manualSorting
                    muiToolbarAlertBannerProps={mrt.isError ? { color: 'error', children: 'Error loading data' } : undefined}
                    onColumnFiltersChange={mrt.setColumnFilters}
                    onColumnFilterFnsChange={mrt.setColumnFiltersMode}
                    onGlobalFilterChange={mrt.setGlobalFilter}
                    onPaginationChange={mrt.setPagination}
                    onSortingChange={mrt.setSorting}
                    initialState={{
                        showColumnFilters: true,
                        density: 'compact',
                    }}
                    state={{
                        columnFilterFns: mrt.columnFilterFns,
                        columnFilters: mrt.columnFilters,
                        globalFilter: mrt.globalFilter,
                        isLoading: mrt.isLoading,
                        pagination: mrt.pagination,
                        showAlertBanner: mrt.isError,
                        showProgressBars: mrt.isRefetching,
                        sorting: mrt.sorting,
                    }}
                    positionExpandColumn="first"
                    positionActionsColumn="first"
                    positionToolbarAlertBanner="bottom"
                    localization={MRT_Localization_PT_BR}
                />
            </ThemeProvider>
        </Box>
    );
}
